/**
 * Canonical role precedence shared across the SRS apps (website + portal).
 *
 * When a user holds multiple roles, the HIGHEST-precedence role drives
 * navigation. Index 0 = highest priority.
 *
 * This MUST stay in sync with the portal's copy at
 * `srswebportal/src/utils/rolePrecedence.ts`. The website works with lowercase
 * role-name strings (as returned by the API), while the portal works with its
 * uppercase UserRole enum — the precedence order is identical.
 */
export type RoleName = 'super_admin' | 'admin' | 'lawyer' | 'inspector' | 'agency' | 'agent' | 'landlord' | 'tenant';

export const ROLE_PRECEDENCE: RoleName[] = [
  'super_admin',
  'admin',
  'lawyer',
  'inspector',
  'agency',
  'agent',
  'landlord',
  'tenant',
];

/** Roles that belong to the admin portal (everything except a pure tenant). */
export const PORTAL_ROLES: RoleName[] = ['super_admin', 'admin', 'lawyer', 'inspector', 'agency', 'agent', 'landlord'];

/** Extract lowercase role names from a user object's roles array. */
export function extractRoleNames(
  user: { roles?: Array<{ name?: string; role_name?: string }> } | null | undefined,
): string[] {
  if (!user?.roles || !Array.isArray(user.roles)) return [];
  return user.roles.map((r) => (r.name ?? r.role_name ?? '').toLowerCase());
}

/**
 * Resolve a user's effective (primary) role using the shared precedence order.
 * Returns null when no recognised role is present.
 */
export function resolvePrimaryRole(
  user: { roles?: Array<{ name?: string; role_name?: string }> } | null | undefined,
): RoleName | null {
  const names = extractRoleNames(user);
  if (names.length === 0) return null;
  return ROLE_PRECEDENCE.find((role) => names.includes(role)) ?? null;
}

/** True when the user's effective role belongs to the admin portal. */
export function isPortalRole(
  user: { roles?: Array<{ name?: string; role_name?: string }> } | null | undefined,
): boolean {
  const primary = resolvePrimaryRole(user);
  return primary !== null && PORTAL_ROLES.includes(primary);
}
